import { type ReactNode } from 'react';

import { cn } from '@apps/web/lib/utils';
import {
    type AdminToolOutputVariant,
    formatLanguageLabel,
    MAX_FILE_CODE_LINES,
    MAX_FILE_DIFF_LINES,
    MAX_INLINE_CODE_LINES,
    MAX_INLINE_DIFF_LINES,
    type ParsedDiffFile,
    type ParsedDiffHunk,
    type ParsedDiffLineKind,
    truncateLines,
} from '@apps/web/routes/_app/admin/agent/tool_output_renderer_shared';

const JSON_TOKEN_PATTERN =
    /("(?:\\.|[^"\\])*"|[-]?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|\btrue\b|\bfalse\b|\bnull\b)/g;

function renderJsonSyntaxLine(line: string): ReactNode {
    const segments: Array<ReactNode> = [];
    let cursor = 0;
    let segmentIndex = 0;
    for (const match of line.matchAll(JSON_TOKEN_PATTERN)) {
        const token = match[0];
        const matchIndex = match.index ?? 0;
        if (matchIndex > cursor) {
            segments.push(line.slice(cursor, matchIndex));
        }

        const afterToken = line.slice(matchIndex + token.length);
        const isKey = token.startsWith('"') && /^\s*:/.test(afterToken);
        const className = isKey
            ? 'font-medium text-[var(--aa-ink)]'
            : token.startsWith('"')
              ? 'text-[var(--aa-ink-soft)]'
              : token === 'true' || token === 'false'
                ? 'font-medium text-[var(--aa-ink-soft)]'
                : token === 'null'
                  ? 'text-[var(--aa-ink-faint)]'
                  : 'text-[var(--aa-ink-muted)]';

        segments.push(
            <span key={`token-${segmentIndex}`} className={className}>
                {token}
            </span>,
        );
        segmentIndex += 1;
        cursor = matchIndex + token.length;
    }

    if (cursor < line.length) {
        segments.push(line.slice(cursor));
    }

    return segments.length > 0 ? segments : line;
}

function tokenizeCodeLine({
    language,
    line,
}: {
    readonly language: string | null;
    readonly line: string;
}): ReadonlyArray<{
    readonly className: string | null;
    readonly text: string;
}> {
    const keywordPattern =
        language === 'python'
            ? /\b(?:and|as|async|await|class|def|elif|else|except|False|for|from|if|import|in|None|not|or|pass|raise|return|True|try|while|with|yield)\b/g
            : language === 'sql'
              ? /\b(?:SELECT|FROM|WHERE|JOIN|LEFT|RIGHT|INNER|OUTER|GROUP|BY|ORDER|LIMIT|UPDATE|DELETE|INSERT|INTO|VALUES|SET|AND|OR|NOT|NULL)\b/gi
              : language === 'bash'
                ? /\b(?:case|do|done|echo|elif|else|esac|export|fi|for|function|if|in|local|then|while)\b/g
                : /\b(?:async|await|break|case|catch|class|const|continue|default|else|export|extends|false|finally|for|from|function|if|import|interface|let|new|null|return|switch|throw|true|try|type|undefined|var|while)\b/g;
    const tokenPattern =
        language === 'python'
            ? /(#.*$|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|\b\d+(?:\.\d+)?\b)/g
            : language === 'sql'
              ? /(--.*$|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|\b\d+(?:\.\d+)?\b)/g
              : /(?:\/\/.*$|\/\*.*\*\/|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|`(?:\\.|[^`\\])*`|\b\d+(?:\.\d+)?\b)/g;
    const segments: Array<{
        readonly className: string | null;
        readonly text: string;
    }> = [];
    let cursor = 0;
    const combinedPattern = new RegExp(
        `${keywordPattern.source}|${tokenPattern.source}`,
        keywordPattern.flags.includes('g')
            ? keywordPattern.flags
            : `${keywordPattern.flags}g`,
    );

    for (const match of line.matchAll(combinedPattern)) {
        const text = match[0];
        const start = match.index ?? 0;
        if (start > cursor) {
            segments.push({
                className: null,
                text: line.slice(cursor, start),
            });
        }
        const className =
            text.startsWith('//') ||
            text.startsWith('#') ||
            text.startsWith('--') ||
            text.startsWith('/*')
                ? 'text-[var(--aa-ink-faint)]'
                : text.startsWith('"') ||
                    text.startsWith("'") ||
                    text.startsWith('`')
                  ? 'text-[var(--aa-success)]'
                  : /^\d/.test(text)
                    ? 'text-[var(--aa-warning)]'
                    : 'font-medium text-[var(--aa-ink-soft)]';
        segments.push({ className, text });
        cursor = start + text.length;
    }

    if (cursor < line.length) {
        segments.push({
            className: null,
            text: line.slice(cursor),
        });
    }

    return segments.length > 0 ? segments : [{ className: null, text: line }];
}

function renderHighlightedCodeLine({
    language,
    line,
}: {
    readonly language: string | null;
    readonly line: string;
}): ReactNode {
    if (language === 'json') {
        return renderJsonSyntaxLine(line);
    }
    return tokenizeCodeLine({ language, line }).map((segment, index) => (
        <span
            key={`${index}-${segment.text}`}
            className={segment.className ?? undefined}
        >
            {segment.text}
        </span>
    ));
}

export function CodeRenderer({
    code,
    language,
    variant,
}: {
    readonly code: string;
    readonly language: string | null;
    readonly variant: AdminToolOutputVariant;
}) {
    const maxLines =
        variant === 'file' ? MAX_FILE_CODE_LINES : MAX_INLINE_CODE_LINES;
    const { hiddenLineCount, visibleLines } = truncateLines({
        text: code,
        maxLines,
    });
    const languageLabel = formatLanguageLabel(language);

    return (
        <div
            data-testid="admin-tool-output-code"
            className="overflow-hidden rounded-[2px] border border-[var(--aa-border)] bg-[var(--aa-surface-sunk)]"
        >
            <div className="flex items-center justify-between border-b border-[var(--aa-border)] bg-white px-3 py-2">
                <span className="aa-mono aa-ink-soft text-[11px] font-medium">
                    {languageLabel ?? 'Code'}
                </span>
                <span className="font-mono text-[10px] text-[var(--aa-ink-faint)] tabular-nums">
                    {visibleLines.length.toLocaleString()} line
                    {visibleLines.length === 1 ? '' : 's'}
                </span>
            </div>
            <div className="max-h-[34rem] overflow-auto">
                <table className="w-full border-separate border-spacing-0">
                    <tbody>
                        {visibleLines.map((line, index) => (
                            <tr
                                key={`${index}-${line}`}
                                className="align-top font-mono text-[11px] leading-5"
                            >
                                <td className="w-11 border-r border-[var(--aa-border)] px-2 py-0.5 text-right text-[var(--aa-ink-faint)] tabular-nums select-none">
                                    {index + 1}
                                </td>
                                <td className="px-3 py-0.5 whitespace-pre-wrap text-[var(--aa-ink)]">
                                    {renderHighlightedCodeLine({
                                        language,
                                        line,
                                    })}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {hiddenLineCount > 0 ? (
                <div className="border-t border-[var(--aa-border)] bg-white px-3 py-2 text-[11px] text-[var(--aa-ink-muted)]">
                    Truncated {hiddenLineCount.toLocaleString()} additional line
                    {hiddenLineCount === 1 ? '' : 's'} for performance.
                </div>
            ) : null}
        </div>
    );
}

function diffLineClass(kind: ParsedDiffLineKind): string {
    switch (kind) {
        case 'add':
            return 'bg-[var(--aa-success-soft)] text-[var(--aa-success)]';
        case 'remove':
            return 'bg-[var(--aa-danger-soft)] text-[var(--aa-danger)]';
        case 'meta':
            return 'bg-[var(--aa-surface-sunk)] text-[var(--aa-ink-muted)]';
        case 'context':
        default:
            return 'bg-white text-[var(--aa-ink)]';
    }
}

export function DiffRenderer({
    files,
    text,
    variant,
}: {
    readonly files: ReadonlyArray<ParsedDiffFile>;
    readonly text: string;
    readonly variant: AdminToolOutputVariant;
}) {
    const maxLines =
        variant === 'file' ? MAX_FILE_DIFF_LINES : MAX_INLINE_DIFF_LINES;
    const totalLineCount = text.split('\n').length;
    let remainingLineBudget = maxLines;

    return (
        <div data-testid="admin-tool-output-diff" className="space-y-3">
            {files.map((file, fileIndex) => {
                const visibleHunks: Array<ParsedDiffHunk> = [];
                let fileVisibleLineCount = 0;
                for (const hunk of file.hunks) {
                    if (remainingLineBudget <= 0) {
                        break;
                    }
                    const visibleLines = hunk.lines.slice(
                        0,
                        remainingLineBudget,
                    );
                    if (visibleLines.length === 0) {
                        break;
                    }
                    visibleHunks.push({
                        header: hunk.header,
                        lines: visibleLines,
                    });
                    remainingLineBudget -= visibleLines.length;
                    fileVisibleLineCount += visibleLines.length;
                }
                return (
                    <details
                        key={`${file.label}-${fileIndex}`}
                        open={fileIndex === 0}
                        className="overflow-hidden rounded-[2px] border border-[var(--aa-border)] bg-[var(--aa-surface-sunk)]"
                    >
                        <summary className="aa-mono aa-ink flex cursor-pointer items-center justify-between bg-white px-3 py-2 text-[12px] font-medium">
                            <span className="truncate">{file.label}</span>
                            <span className="font-mono text-[10px] text-[var(--aa-ink-faint)]">
                                {formatLanguageLabel(file.language) ?? 'Patch'}
                            </span>
                        </summary>
                        <div className="border-t border-[var(--aa-border)]">
                            {visibleHunks.map((hunk, hunkIndex) => (
                                <div
                                    key={`${file.label}-hunk-${hunkIndex}`}
                                    className={cn(
                                        'border-b border-[var(--aa-border)]',
                                        hunkIndex === visibleHunks.length - 1 &&
                                            'border-b-0',
                                    )}
                                >
                                    <div className="bg-[var(--aa-surface-sunk)] px-3 py-1 font-mono text-[10px] text-[var(--aa-ink-muted)]">
                                        {hunk.header}
                                    </div>
                                    <div className="max-h-[28rem] overflow-auto">
                                        {hunk.lines.map((line, lineIndex) => (
                                            <div
                                                key={`${line.text}-${lineIndex}`}
                                                className={cn(
                                                    'flex gap-3 px-3 py-0.5 font-mono text-[11px] leading-5 whitespace-pre-wrap',
                                                    diffLineClass(line.kind),
                                                )}
                                            >
                                                <span className="text-[var(--aa-ink-faint)] select-none">
                                                    {line.kind === 'add'
                                                        ? '+'
                                                        : line.kind === 'remove'
                                                          ? '-'
                                                          : line.kind === 'meta'
                                                            ? '·'
                                                            : ' '}
                                                </span>
                                                <span className="flex-1">
                                                    {renderHighlightedCodeLine({
                                                        language: file.language,
                                                        line:
                                                            line.kind ===
                                                                'add' ||
                                                            line.kind ===
                                                                'remove' ||
                                                            line.kind ===
                                                                'context'
                                                                ? line.text.slice(
                                                                      1,
                                                                  )
                                                                : line.text,
                                                    })}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                            {fileVisibleLineCount === 0 ? (
                                <div className="px-3 py-2 text-[11px] text-[var(--aa-ink-muted)]">
                                    No patch lines in this file.
                                </div>
                            ) : null}
                        </div>
                    </details>
                );
            })}
            {totalLineCount > maxLines ? (
                <div className="rounded-[2px] border border-[var(--aa-border)] bg-white px-3 py-2 text-[11px] text-[var(--aa-ink-muted)]">
                    Truncated {(totalLineCount - maxLines).toLocaleString()}{' '}
                    additional line{totalLineCount - maxLines === 1 ? '' : 's'}.
                </div>
            ) : null}
        </div>
    );
}
